/**
 * Devanagari ↔ Latin in two systems: IAST and ISO 15919.
 *
 * Sources:
 *
 * - IAST — the International Alphabet of Sanskrit Transliteration (Sanskrit scholars'
 *   standard since the 1894 Geneva congress): long vowels with a macron, retroflexes and the
 *   vocalic r and l with a dot below (ṭ ḍ ṇ ṣ, ṛ ṝ ḷ ḹ), ṅ ñ ś, anusvāra ṃ, visarga ḥ, one
 *   Latin letter or digraph per Devanagari letter, e and o for the long vowels ए and ओ.
 * - ISO 15919:2001 — "Transliteration of Devanagari and related Indic scripts into Latin
 *   characters". Same consonants; vocalic r and l written r̥ r̥̄ l̥ l̥̄ (ring below), ṛ kept for the
 *   Hindi flap ड़, ळ written ḷ, long e and o written ē and ō, anusvāra ṁ (the "simplified" form;
 *   the strict form uses the nasal of the next consonant's class), candrabindu as a tilde over
 *   the vowel (with m̐ as the fallback). The standard itself is paid; the values here were
 *   checked against the Library of Congress "Hindi Romanization Table" (2025 version,
 *   loc.gov/catdir/cpso/romanization/hindi.pdf), the UNGEGN Hindi romanization report
 *   (version 4.0, 2016) and the published ISO 15919 comparison tables.
 * - The option to write a nasal letter for the anusvāra follows Note 4 of the Library of
 *   Congress Hindi table (ṅ before gutturals, ñ before palatals, ṇ before cerebrals, n before
 *   dentals, m before labials).
 *
 * What a program cannot do: both systems are transliterations, so the inherent "a" is written
 * after every consonant — kamala, not kamal — and no medial schwa deletion is attempted. The
 * "final a" option only drops the last one, which is the most regular part of Hindi
 * pronunciation. Latin → Devanagari cannot tell a plain consonant from one with a final virama,
 * and the sequences kh, gh, ch… are always read as one aspirated letter.
 */

export type DevanagariSystem = "iast" | "iso";
export type AnusvaraMode = "mark" | "class";
export type FinalA = "keep" | "drop";
export type FinalConsonant = "bare" | "halanta";

export interface DevanagariOptions {
  /** `class` writes the nasal of the next consonant's class instead of ṃ / ṁ. */
  anusvara: AnusvaraMode;
  /** `drop` leaves out the inherent a at the end of a word (Hindi pronunciation). */
  finalA: FinalA;
  /** ISO 15919: write long e and o as plain e and o. */
  plainEO: boolean;
  capitalize: boolean;
  /** Latin → Devanagari: a word-final consonant with no vowel gets a virama or stays bare. */
  finalConsonant: FinalConsonant;
}

export interface DevanagariResult {
  text: string;
  /** Characters the converter did not recognise and left unchanged. */
  unknown: string[];
}

const VIRAMA = "्";
const NUKTA = "़";
const ANUSVARA = "ं";
const CANDRABINDU = "ँ";
const VISARGA = "ः";
const AVAGRAHA = "ऽ";
const OM = "ॐ";
const TILDE = "̃";

/** Independent vowel, vowel sign, IAST, ISO 15919. */
const VOWELS: readonly (readonly [string, string, string, string])[] = [
  ["अ", "", "a", "a"],
  ["आ", "ा", "ā", "ā"],
  ["इ", "ि", "i", "i"],
  ["ई", "ी", "ī", "ī"],
  ["उ", "ु", "u", "u"],
  ["ऊ", "ू", "ū", "ū"],
  ["ऋ", "ृ", "ṛ", "r̥"],
  ["ॠ", "ॄ", "ṝ", "r̥̄"],
  ["ऌ", "ॢ", "ḷ", "l̥"],
  ["ॡ", "ॣ", "ḹ", "l̥̄"],
  ["ए", "े", "e", "ē"],
  ["ऐ", "ै", "ai", "ai"],
  ["ओ", "ो", "o", "ō"],
  ["औ", "ौ", "au", "au"],
  ["ऍ", "ॅ", "ê", "ê"],
  ["ऑ", "ॉ", "ô", "ô"],
  ["ऎ", "ॆ", "ĕ", "e"],
  ["ऒ", "ॊ", "ŏ", "o"],
];

/** Consonants shared by both systems. */
const CONSONANTS: readonly (readonly [string, string])[] = [
  ["क", "k"],
  ["ख", "kh"],
  ["ग", "g"],
  ["घ", "gh"],
  ["ङ", "ṅ"],
  ["च", "c"],
  ["छ", "ch"],
  ["ज", "j"],
  ["झ", "jh"],
  ["ञ", "ñ"],
  ["ट", "ṭ"],
  ["ठ", "ṭh"],
  ["ड", "ḍ"],
  ["ढ", "ḍh"],
  ["ण", "ṇ"],
  ["त", "t"],
  ["थ", "th"],
  ["द", "d"],
  ["ध", "dh"],
  ["न", "n"],
  ["प", "p"],
  ["फ", "ph"],
  ["ब", "b"],
  ["भ", "bh"],
  ["म", "m"],
  ["य", "y"],
  ["र", "r"],
  ["ल", "l"],
  ["व", "v"],
  ["श", "ś"],
  ["ष", "ṣ"],
  ["स", "s"],
  ["ह", "h"],
];

/** Letters that differ: Vedic/Marathi ळ. */
const LLA: Record<DevanagariSystem, string> = { iast: "ḻ", iso: "ḷ" };

/** Consonants with a nukta (used for Urdu loanwords in Hindi), the same in both tables. */
const NUKTA_CONSONANTS: readonly (readonly [string, string])[] = [
  ["क", "q"],
  ["ख", "k͟h"],
  ["ग", "ġ"],
  ["ज", "z"],
  ["झ", "ž"],
  ["ड", "ṛ"],
  ["ढ", "ṛh"],
  ["फ", "f"],
  ["य", "ẏ"],
];

const WORD_RUN = /[ऀ-ॣॱ-ॿ‌‍]+/g;
const FORMAT_CHARS = /[‌‍]/g;

const OTHER_SYMBOLS: Record<string, string> = {
  "।": ".",
  "॥": "||",
  "॰": ".",
};
const DIGIT_ZERO = 0x9_66;
const DIGIT_NINE = 0x9_6f;
const SYMBOL_OR_DIGIT = /[।॥०-९॰]/g;

type Kind =
  | "C"
  | "V"
  | "M"
  | "VIR"
  | "ANU"
  | "CAN"
  | "VIS"
  | "AVA"
  | "OM"
  | "X";

interface Tok {
  kind: Kind;
  /** The Devanagari character(s); for a consonant with nukta, base + nukta. */
  dev: string;
}

const SYSTEM_COLUMN: Record<DevanagariSystem, 2 | 3> = { iast: 2, iso: 3 };

function consonantTable(system: DevanagariSystem): Map<string, string> {
  const table = new Map<string, string>(CONSONANTS);
  table.set("ळ", LLA[system]);
  for (const [dev, latin] of NUKTA_CONSONANTS) {
    table.set(dev + NUKTA, latin);
  }
  return table;
}

const FORWARD_CONSONANTS: Record<DevanagariSystem, Map<string, string>> = {
  iast: consonantTable("iast"),
  iso: consonantTable("iso"),
};

const INDEPENDENT = new Map<string, readonly string[]>();
const MATRAS = new Map<string, readonly string[]>();
for (const row of VOWELS) {
  INDEPENDENT.set(row[0], row);
  if (row[1]) {
    MATRAS.set(row[1], row);
  }
}

function vowelValue(
  row: readonly string[],
  system: DevanagariSystem,
  options: DevanagariOptions
): string {
  const value = row[SYSTEM_COLUMN[system]] as string;
  if (system === "iso" && options.plainEO) {
    if (value === "ē") {
      return "e";
    }
    if (value === "ō") {
      return "o";
    }
  }
  return value;
}

function tokenize(word: string): Tok[] {
  // NFD splits the composed nukta letters (क़ ड़ ऩ …) into a base letter and the nukta.
  const text = word.normalize("NFD").replace(FORMAT_CHARS, "");
  const chars = [...text];
  const toks: Tok[] = [];
  for (let i = 0; i < chars.length; i += 1) {
    const ch = chars[i] as string;
    if (FORWARD_CONSONANTS.iast.has(ch)) {
      if (chars[i + 1] === NUKTA) {
        toks.push({ kind: "C", dev: ch + NUKTA });
        i += 1;
      } else {
        toks.push({ kind: "C", dev: ch });
      }
    } else if (INDEPENDENT.has(ch)) {
      toks.push({ kind: "V", dev: ch });
    } else if (MATRAS.has(ch)) {
      toks.push({ kind: "M", dev: ch });
    } else {
      toks.push({ kind: markKind(ch), dev: ch });
    }
  }
  return toks;
}

function markKind(ch: string): Kind {
  switch (ch) {
    case VIRAMA:
      return "VIR";
    case ANUSVARA:
      return "ANU";
    case CANDRABINDU:
    case "ऀ":
      return "CAN";
    case VISARGA:
      return "VIS";
    case AVAGRAHA:
      return "AVA";
    case OM:
      return "OM";
    default:
      return "X";
  }
}

/** The nasal letter for an anusvāra before the consonant `dev`, or "" when it stays a mark. */
function classNasal(dev: string | undefined): string {
  const code = (dev ?? "").codePointAt(0) ?? 0;
  if (code >= 0x9_15 && code <= 0x9_19) {
    return "ṅ";
  }
  if (code >= 0x9_1a && code <= 0x9_1e) {
    return "ñ";
  }
  if (code >= 0x9_1f && code <= 0x9_23) {
    return "ṇ";
  }
  if (code >= 0x9_24 && code <= 0x9_28) {
    return "n";
  }
  if (code >= 0x9_2a && code <= 0x9_2e) {
    return "m";
  }
  return "";
}

const ASPIRATE_STOPS = new Set([..."kgcjṭḍtdpb"]);

interface Emit {
  text: string;
  /** True when the text ends in a vowel a tilde can sit on (ISO 15919 candrabindu). */
  vowel: boolean;
}

const NASALIZABLE = /[aāiīuūeēoōêô]$/;

function emitConsonantUnit(
  toks: readonly Tok[],
  j: number,
  ctx: { system: DevanagariSystem; options: DevanagariOptions; nuclei: number }
): { emit: Emit; used: number } {
  const tok = toks[j] as Tok;
  const next = toks[j + 1];
  const base =
    FORWARD_CONSONANTS[ctx.system].get(tok.dev) ??
    FORWARD_CONSONANTS[ctx.system].get(tok.dev[0] ?? "") ??
    "";
  if (next?.kind === "M") {
    const row = MATRAS.get(next.dev) as readonly string[];
    const value = vowelValue(row, ctx.system, ctx.options);
    return {
      emit: { text: base + value, vowel: NASALIZABLE.test(value) },
      used: 2,
    };
  }
  if (next?.kind === "VIR") {
    return { emit: { text: base, vowel: false }, used: 2 };
  }
  const last = j === toks.length - 1;
  if (last && ctx.options.finalA === "drop" && ctx.nuclei >= 2) {
    return { emit: { text: base, vowel: false }, used: 1 };
  }
  return { emit: { text: `${base}a`, vowel: true }, used: 1 };
}

function emitMark(
  tok: Tok,
  next: Tok | undefined,
  prev: Emit,
  ctx: { system: DevanagariSystem; options: DevanagariOptions }
): Emit {
  const anusvara = ctx.system === "iast" ? "ṃ" : "ṁ";
  switch (tok.kind) {
    case "ANU": {
      const nasal =
        ctx.options.anusvara === "class" && next?.kind === "C"
          ? classNasal(next.dev)
          : "";
      return { text: nasal || anusvara, vowel: false };
    }
    case "CAN":
      if (ctx.system === "iso" && prev.vowel) {
        return { text: TILDE, vowel: false };
      }
      return { text: "m̐", vowel: false };
    case "VIS":
      return { text: "ḥ", vowel: false };
    case "AVA":
      return { text: "’", vowel: false };
    case "OM": {
      const o = ctx.system === "iso" && !ctx.options.plainEO ? "ō" : "o";
      return { text: o + anusvara, vowel: false };
    }
    default:
      return { text: tok.dev, vowel: false };
  }
}

function countNuclei(toks: readonly Tok[]): number {
  let count = 0;
  for (let j = 0; j < toks.length; j += 1) {
    const tok = toks[j] as Tok;
    if (tok.kind === "V" || (tok.kind === "C" && toks[j + 1]?.kind !== "VIR")) {
      count += 1;
    }
  }
  return count;
}

function needsSeparator(out: string, tok: Tok | undefined): boolean {
  if (tok?.kind !== "C" || tok.dev !== "ह") {
    return false;
  }
  const last = out.normalize("NFC").at(-1) ?? "";
  return ASPIRATE_STOPS.has(last);
}

function romanizeWord(
  word: string,
  system: DevanagariSystem,
  options: DevanagariOptions,
  unknown: Set<string>
): string {
  const toks = tokenize(word);
  const ctx = { system, options, nuclei: countNuclei(toks) };
  let out = "";
  let prev: Emit = { text: "", vowel: false };
  let j = 0;
  while (j < toks.length) {
    const tok = toks[j] as Tok;
    let emit: Emit;
    let used = 1;
    if (tok.kind === "C") {
      const unit = emitConsonantUnit(toks, j, ctx);
      emit = unit.emit;
      used = unit.used;
      if (used === 2 && toks[j + 1]?.kind === "VIR") {
        const after = toks[j + 2];
        if (needsSeparator(emit.text, after)) {
          emit = { text: `${emit.text}-`, vowel: false };
        }
      }
    } else if (tok.kind === "V" || tok.kind === "M") {
      const row = (INDEPENDENT.get(tok.dev) ??
        MATRAS.get(tok.dev)) as readonly string[];
      const value = vowelValue(row, system, options);
      emit = { text: value, vowel: NASALIZABLE.test(value) };
    } else if (tok.kind === "X" || tok.kind === "VIR") {
      unknown.add(tok.dev);
      emit = { text: tok.dev, vowel: false };
    } else {
      emit = emitMark(tok, toks[j + 1], prev, { system, options });
    }
    out += emit.text;
    prev = emit;
    j += used;
  }
  return options.capitalize ? capitalizeFirst(out) : out;
}

function capitalizeFirst(text: string): string {
  const first = [...text][0] ?? "";
  return first.toUpperCase() + text.slice(first.length);
}

function mapSymbol(ch: string): string {
  const mapped = OTHER_SYMBOLS[ch];
  if (mapped) {
    return mapped;
  }
  const code = ch.codePointAt(0) ?? 0;
  if (code >= DIGIT_ZERO && code <= DIGIT_NINE) {
    return String(code - DIGIT_ZERO);
  }
  return ch;
}

/** Romanize Devanagari text; anything that is not Devanagari passes through. */
export function romanizeDevanagari(
  input: string,
  system: DevanagariSystem,
  options: DevanagariOptions
): DevanagariResult {
  const unknown = new Set<string>();
  const text = input
    .replace(WORD_RUN, (word) => romanizeWord(word, system, options, unknown))
    .replace(SYMBOL_OR_DIGIT, mapSymbol);
  return { text: text.normalize("NFC"), unknown: [...unknown] };
}

// ---------------------------------------------------------------------------------------
// Latin → Devanagari
// ---------------------------------------------------------------------------------------

type RKind = "C" | "V" | "ANU" | "CAN" | "VIS" | "AVA";

interface RTok {
  kind: RKind;
  /** Consonant letter (with nukta), independent vowel, or sign. */
  dev: string;
  /** Vowel sign for a vowel. */
  matra?: string;
}

function reverseTable(system: DevanagariSystem): Map<string, RTok> {
  const table = new Map<string, RTok>();
  const put = (latin: string, tok: RTok) => {
    table.set(latin.normalize("NFD"), tok);
  };
  const column = SYSTEM_COLUMN[system];
  for (const row of VOWELS) {
    put(row[column] as string, { kind: "V", dev: row[0], matra: row[1] });
  }
  if (system === "iso") {
    // Plain e and o are the long vowels ए and ओ unless the short signs are written.
    put("e", { kind: "V", dev: "ए", matra: "े" });
    put("o", { kind: "V", dev: "ओ", matra: "ो" });
  }
  for (const [dev, latin] of CONSONANTS) {
    put(latin, { kind: "C", dev });
  }
  put(LLA[system], { kind: "C", dev: "ळ" });
  for (const [dev, latin] of NUKTA_CONSONANTS) {
    if (system === "iast" && latin.startsWith("ṛ")) {
      continue;
    }
    put(latin, { kind: "C", dev: dev + NUKTA });
  }
  put("ṃ", { kind: "ANU", dev: ANUSVARA });
  put("ṁ", { kind: "ANU", dev: ANUSVARA });
  put("m̐", { kind: "CAN", dev: CANDRABINDU });
  put("ḥ", { kind: "VIS", dev: VISARGA });
  put("’", { kind: "AVA", dev: AVAGRAHA });
  put("'", { kind: "AVA", dev: AVAGRAHA });
  return table;
}

const REVERSE: Record<DevanagariSystem, Map<string, RTok>> = {
  iast: reverseTable("iast"),
  iso: reverseTable("iso"),
};

const MAX_KEY = 3;
const LETTER = /\p{L}/u;
const TILDE_MARK = "̃";

function matchAt(
  low: readonly string[],
  at: number,
  table: Map<string, RTok>
): { tok: RTok; length: number } | undefined {
  for (
    let length = Math.min(MAX_KEY, low.length - at);
    length > 0;
    length -= 1
  ) {
    const tok = table.get(low.slice(at, at + length).join(""));
    if (tok) {
      return { tok, length };
    }
  }
  return;
}

interface ReverseState {
  out: string;
  /** A consonant was written and has not yet received a vowel. */
  pending: boolean;
  /** The previous token was a vowel, so a combining tilde nasalizes it. */
  lastVowel: boolean;
  unknown: Set<string>;
}

function addToken(state: ReverseState, tok: RTok): void {
  state.lastVowel = tok.kind === "V";
  if (tok.kind === "C") {
    if (state.pending) {
      state.out += VIRAMA;
    }
    state.out += tok.dev;
    state.pending = true;
  } else if (tok.kind === "V") {
    state.out += state.pending ? (tok.matra ?? "") : tok.dev;
    state.pending = false;
  } else {
    state.pending = false;
    state.out += tok.dev;
  }
}

function releaseConsonant(state: ReverseState, options: DevanagariOptions) {
  if (state.pending && options.finalConsonant === "halanta") {
    state.out += VIRAMA;
  }
  state.pending = false;
}

/** Handle a character that is not part of any token; returns how many characters it used. */
function addOther(
  state: ReverseState,
  chars: readonly string[],
  low: readonly string[],
  i: number,
  table: Map<string, RTok>,
  options: DevanagariOptions
): number {
  const ch = chars[i] as string;
  if (ch === TILDE_MARK && state.lastVowel) {
    state.out += CANDRABINDU;
    state.lastVowel = false;
    return 1;
  }
  if (
    ch === "-" &&
    state.pending &&
    matchAt(low, i + 1, table)?.tok.kind === "C"
  ) {
    // A hyphen between two consonants keeps them apart (d-h is द्ह, not ध).
    return 1;
  }
  releaseConsonant(state, options);
  state.lastVowel = false;
  if (LETTER.test(ch)) {
    state.unknown.add(ch.normalize("NFC"));
  }
  state.out += ch;
  return 1;
}

/** Convert IAST or ISO 15919 Latin text to Devanagari. */
export function latinToDevanagari(
  input: string,
  system: DevanagariSystem,
  options: DevanagariOptions
): DevanagariResult {
  const table = REVERSE[system];
  const chars = [...input.normalize("NFD")];
  const low = chars.map((c) => c.toLowerCase());
  const state: ReverseState = {
    out: "",
    pending: false,
    lastVowel: false,
    unknown: new Set<string>(),
  };
  let i = 0;
  while (i < chars.length) {
    const hit = matchAt(low, i, table);
    if (hit) {
      addToken(state, hit.tok);
      i += hit.length;
    } else {
      i += addOther(state, chars, low, i, table, options);
    }
  }
  releaseConsonant(state, options);
  return { text: state.out.normalize("NFC"), unknown: [...state.unknown] };
}

// ---------------------------------------------------------------------------------------
// Letter table
// ---------------------------------------------------------------------------------------

export interface DevanagariLetterRow {
  group: "vowel" | "consonant" | "nukta" | "sign";
  dev: string;
  iast: string;
  iso: string;
}

const DOTTED_CIRCLE = "◌";

/** The reference table: vowels (independent form and sign), consonants, nukta letters and marks. */
export function devanagariLetterRows(): DevanagariLetterRow[] {
  const rows: DevanagariLetterRow[] = [];
  for (const [independent, sign, iast, iso] of VOWELS) {
    rows.push({
      group: "vowel",
      dev: sign ? `${independent} ${DOTTED_CIRCLE}${sign}` : independent,
      iast,
      iso,
    });
  }
  for (const [dev, latin] of CONSONANTS) {
    rows.push({ group: "consonant", dev, iast: latin, iso: latin });
  }
  rows.push({ group: "consonant", dev: "ळ", iast: LLA.iast, iso: LLA.iso });
  for (const [dev, latin] of NUKTA_CONSONANTS) {
    rows.push({ group: "nukta", dev: dev + NUKTA, iast: latin, iso: latin });
  }
  rows.push(
    { group: "sign", dev: `${DOTTED_CIRCLE}${ANUSVARA}`, iast: "ṃ", iso: "ṁ" },
    {
      group: "sign",
      dev: `${DOTTED_CIRCLE}${CANDRABINDU}`,
      iast: "m̐",
      iso: "◌̃  (m̐)",
    },
    { group: "sign", dev: `${DOTTED_CIRCLE}${VISARGA}`, iast: "ḥ", iso: "ḥ" },
    { group: "sign", dev: AVAGRAHA, iast: "’", iso: "’" },
    { group: "sign", dev: OM, iast: "oṃ", iso: "ōṁ" }
  );
  return rows;
}

export const DEVANAGARI_SYSTEMS: readonly DevanagariSystem[] = ["iast", "iso"];
